
/**
 * Custom Mega Menu Interactions
 */
class CustomMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.mega-menu__content');
    this.inner = this.querySelector('.mega-menu__inner');
    this.productSelectionCache = new Map();
    this.productVariantsCache = new WeakMap();
    this.colorsApplied = false;
    this.closeTimer = null;
    this.openTimer = null;
    this.closeAnimationTimer = null;

    if (window.matchMedia('(min-width: 990px)').matches) {
      this.initDesktop();
    } else {
      this.initMobile();
    }
  }

  initDesktop() {
    this.addEventListener('mouseenter', () => this.open());
    this.addEventListener('mouseleave', () => this.queueClose());
    
    // Keep desktop click behavior on the same animated path as hover.
    this.summary.addEventListener('click', (e) => {
      e.preventDefault();
      this.open();
    });
  }

  initMobile() {
    // Mobile uses default details behavior (click to toggle)
    // We can add logic here to prevent double-taps if necessary
  }

  open() {
    this.closeSiblingMenus();
    this.cancelClose();
    window.clearTimeout(this.closeAnimationTimer);
    this.classList.remove('is-closing');
    this.details.setAttribute('open', '');
    this.content.style.display = 'block';
    if (!this.colorsApplied) {
      this.applyColorSelections();
      this.colorsApplied = true;
    }
    window.clearTimeout(this.openTimer);
    this.openTimer = window.setTimeout(() => {
        this.classList.add('is-open');
        this.content.style.opacity = '1';
        this.content.style.transform = 'translateY(0)';
        if (this.inner) {
          this.inner.style.opacity = '1';
          this.inner.style.transform = 'translateY(0)';
        }
    }, 10);
  }

  closeSiblingMenus() {
    const menuList = this.closest('.list-menu--inline');
    if (!menuList) return;

    menuList.querySelectorAll('custom-mega-menu').forEach((menu) => {
      if (menu !== this && typeof menu.closeImmediately === 'function') {
        menu.closeImmediately();
      }
    });
  }

  queueClose() {
    this.cancelClose();
    this.closeTimer = window.setTimeout(() => this.close(), 180);
  }

  cancelClose() {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  close() {
    if (this.matches(':hover')) return;

    window.clearTimeout(this.openTimer);
    this.classList.remove('is-open');
    this.classList.add('is-closing');
    this.content.style.opacity = '0';
    this.content.style.transform = 'translateY(-0.4rem)';
    if (this.inner) {
      this.inner.style.opacity = '0';
      this.inner.style.transform = 'translateY(-0.2rem)';
    }
    window.clearTimeout(this.closeAnimationTimer);
    this.closeAnimationTimer = window.setTimeout(() => {
        if (!this.matches(':hover')) {
            this.classList.remove('is-closing');
            this.details.removeAttribute('open');
            this.content.style.display = 'none';
        }
    }, 400);
  }

  closeImmediately() {
    window.clearTimeout(this.closeTimer);
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeAnimationTimer);
    this.classList.remove('is-open', 'is-closing');
    this.details.removeAttribute('open');
    this.content.style.display = 'none';
    this.content.style.opacity = '0';
    this.content.style.transform = 'translateY(-0.4rem)';
    if (this.inner) {
      this.inner.style.opacity = '0';
      this.inner.style.transform = 'translateY(-0.2rem)';
    }
  }

  applyColorSelections() {
    this.querySelectorAll('[data-mega-menu-product-card]').forEach((card) => {
      this.applyProductCardSelection(card);
    });

    this.querySelectorAll('[data-mega-menu-explore-card]').forEach((card) => {
      this.applyExploreCardSelection(card);
    });
  }

  applyProductCardSelection(card) {
    const selection = this.getProductSelection(card);
    const variant = selection?.variant;

    if (!variant) return;

    const image = card.querySelector('[data-mega-menu-product-image]');
    const link = card.querySelector('[data-mega-menu-product-link]');

    if (image && variant.featured_image) {
      image.src = variant.featured_image;
    }

    if (link && variant.url) {
      link.href = variant.url;
    }
  }

  applyExploreCardSelection(card) {
    const selection = this.getProductSelection(card);
    const variant = selection?.variant;

    if (!variant) return;

    const image = card.querySelector('[data-mega-menu-explore-image]');
    const links = card.querySelectorAll('[data-mega-menu-explore-link]');
    const selectedColorKey = this.normalizeColor(selection.color);
    const colorIndex = this.getColorOptionIndex(card);
    const variants = this.getVariants(card);

    const sameColorCandidates = variants.filter((candidate) => (
      this.normalizeColor(candidate.options?.[colorIndex]) === selectedColorKey
    ));

    const orderedCandidates = this.uniqueVariants([
      ...sameColorCandidates.filter((candidate) => candidate.id === variant.id),
      ...sameColorCandidates.filter((candidate) => candidate.available && candidate.id !== variant.id),
      ...sameColorCandidates.filter((candidate) => candidate.id !== variant.id),
      variant,
      ...variants.filter((candidate) => candidate.available),
      ...variants,
    ]);

    const bestVariant = orderedCandidates.find((candidate) => this.getExploreImage(candidate))
      || variant;
    const bestImage = this.getExploreImage(bestVariant);

    if (image && bestImage) {
      image.src = bestImage;
    }

    if (card.dataset.linkMode === 'variant' && bestVariant.url) {
      links.forEach((link) => {
        link.href = bestVariant.url;
      });
    }
  }

  getProductSelection(card) {
    const productId = card.dataset.productId;
    if (!productId) return null;

    if (this.productSelectionCache.has(productId)) {
      return this.productSelectionCache.get(productId);
    }

    const variants = this.getVariants(card);
    if (!variants.length) return null;

    const colorIndex = this.getColorOptionIndex(card);
    const collectionColors = this.getCollectionColors(card);
    const availableColors = this.getAvailableColors(variants, collectionColors, colorIndex);

    let selectedColor = null;
    if (collectionColors.length > 0 && availableColors.length > 0) {
      selectedColor = availableColors[Math.floor(Math.random() * availableColors.length)];
    }

    let variant = null;
    if (selectedColor) {
      const selectedColorKey = this.normalizeColor(selectedColor);
      variant = variants.find((candidate) => (
        candidate.available
        && this.normalizeColor(candidate.options?.[colorIndex]) === selectedColorKey
      )) || variants.find((candidate) => (
        this.normalizeColor(candidate.options?.[colorIndex]) === selectedColorKey
      ));
    }

    if (!variant) {
      variant = variants.find((candidate) => candidate.available) || variants[0];
    }

    const resolvedColor = variant?.options?.[colorIndex] || selectedColor;
    const selection = {variant, color: resolvedColor};
    this.productSelectionCache.set(productId, selection);

    return selection;
  }

  getVariants(card) {
    if (this.productVariantsCache.has(card)) {
      return this.productVariantsCache.get(card);
    }

    const script = card.querySelector('.mega-menu__variants-data');
    let variants = [];

    if (script?.textContent?.trim()) {
      try {
        variants = JSON.parse(script.textContent);
      } catch (error) {
        console.error('Failed to parse mega menu variants data', error);
      }
    }

    this.productVariantsCache.set(card, variants);
    return variants;
  }

  getCollectionColors(card) {
    const rawColors = card.dataset.collectionColors;
    if (!rawColors || rawColors === 'null') {
      return [];
    }

    try {
      const parsedColors = JSON.parse(rawColors);
      if (!Array.isArray(parsedColors)) {
        return [];
      }

      return parsedColors
        .map((color) => {
          if (typeof color === 'string') return color;
          return color?.label || color?.name || color?.value || '';
        })
        .filter(Boolean);
    } catch (error) {
      console.error('Failed to parse mega menu collection colors', error);
      return [];
    }
  }

  getAvailableColors(variants, collectionColors, colorIndex) {
    const seenAvailableColors = new Set();
    const availableColors = [];

    variants.forEach((variant) => {
      if (!variant?.available) return;

      const color = variant.options?.[colorIndex];
      const colorKey = this.normalizeColor(color);
      if (!colorKey || seenAvailableColors.has(colorKey)) return;

      seenAvailableColors.add(colorKey);
      availableColors.push(color);
    });

    const filteredColors = collectionColors.filter((color) => (
      seenAvailableColors.has(this.normalizeColor(color))
    ));

    return filteredColors.length > 0 ? filteredColors : availableColors;
  }

  getColorOptionIndex(card) {
    const optionPosition = Number.parseInt(card.dataset.colorOptionPosition || '1', 10);
    return Number.isNaN(optionPosition) ? 0 : Math.max(optionPosition - 1, 0);
  }

  getExploreImage(variant) {
    if (!variant) return null;

    if (Array.isArray(variant.lifestyle_images)) {
      return variant.lifestyle_images[1] || variant.lifestyle_images[0] || variant.featured_image || null;
    }

    return variant.featured_image || null;
  }

  uniqueVariants(variants) {
    const seenIds = new Set();
    return variants.filter((variant) => {
      if (!variant?.id || seenIds.has(variant.id)) return false;
      seenIds.add(variant.id);
      return true;
    });
  }

  normalizeColor(value) {
    return String(value || '').trim().toLowerCase();
  }
}

if (!customElements.get('custom-mega-menu')) {
  customElements.define('custom-mega-menu', CustomMegaMenu);
}
